import { useState, useEffect, useRef, memo } from "react";
import { Clock as ClockIcon } from "lucide-react";

export const Clock = memo(function Clock() {
    const [currentTime, setCurrentTime] = useState(() => new Date());
    const intervalRef = useRef(null);
    
    // Load saved position from localStorage or use default
    const getInitialPosition = () => {
        const saved = localStorage.getItem('clock-position');
        if (saved) {
            return JSON.parse(saved);
        }
        // Default: top center
        return { x: window.innerWidth / 2, y: 4 };
    };
    
    const [position, setPosition] = useState(getInitialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const clockRef = useRef(null);

    useEffect(() => {
        // Update time every second, but only update if the second has actually changed
        intervalRef.current = setInterval(() => {
            const now = new Date();
            setCurrentTime(prev => {
                // Only update if the second has changed (prevents unnecessary re-renders)
                if (now.getSeconds() !== prev.getSeconds()) {
                    return now;
                }
                return prev;
            });
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);


    // Handle mouse down to start dragging
    const handleMouseDown = (e) => {
        if (!clockRef.current) return;
        
        const rect = clockRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    };

    // Handle mouse move while dragging
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Keep clock within viewport bounds
            const maxX = window.innerWidth - (clockRef.current?.offsetWidth || 0);
            const maxY = window.innerHeight - (clockRef.current?.offsetHeight || 0);
            
            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            // Save position to localStorage
            localStorage.setItem('clock-position', JSON.stringify(position));
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, position]);


    // Format time for different timezones using Intl API
    const formatTime = (timezone, label) => {
        const timeFormatter = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });

        const timeString = timeFormatter.format(currentTime);
        
        return { timeString, label };
    };

    const localTime = formatTime(Intl.DateTimeFormat().resolvedOptions().timeZone, "Local");
    const canadaTime = formatTime("America/Toronto", "Canada");
    const ukTime = formatTime("Europe/London", "UK");

    const timezones = [localTime, canadaTime, ukTime];

    return (
        <>
            <div 
                ref={clockRef}
                className={`fixed z-50 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg ${
                    isDragging ? 'cursor-grabbing' : 'cursor-move'
                }`}
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    transform: 'none',
                    cursor: isDragging ? 'grabbing' : 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'48\' height=\'48\' viewport=\'0 0 48 48\' style=\'font-size:32px;\'><text y=\'32\'>🐱</text></svg>") 16 0, move'
                }}
                onMouseDown={handleMouseDown}
            >
                <div className="px-4 py-2">
                    <div className="flex items-center justify-center space-x-6">
                        {timezones.map((tz, index) => (
                            <div key={index} className="flex flex-col items-center space-y-1">
                                <div className="flex items-center space-x-2">
                                    {index === 0 && (
                                        <ClockIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                    )}
                                    <span className="text-xs text-gray-500 font-medium">{tz.label}</span>
                                </div>
                                <span className="text-sm font-mono text-gray-700 tabular-nums font-semibold">
                                    {tz.timeString}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
});

